import { forwardRef, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react'
import { cn } from '@/lib/utils'

const CONTROL =
  'w-full rounded-[var(--radius-control)] border border-line bg-paper px-3 text-sm text-ink ' +
  'placeholder:text-ink-faint transition-colors focus:border-brand focus:outline-none ' +
  'disabled:cursor-not-allowed disabled:bg-paper-muted disabled:text-ink-muted'

/** A field in error must be unmistakable at a glance, not just a thin border change. */
export const INVALID_CONTROL = 'border-danger bg-danger-soft focus:border-danger'

export interface FieldProps {
  label?: string
  htmlFor?: string
  required?: boolean
  hint?: string
  error?: string
  /** Unit rendered inside the right edge of the control, e.g. `g/m²` or `°C`. */
  suffix?: string
  className?: string
  children: ReactNode
}

export function Field({ label, htmlFor, required, hint, error, className, children }: FieldProps) {
  return (
    <div className={cn('space-y-1.5', className)}>
      {label && (
        <label htmlFor={htmlFor} className="field-label block">
          {label}
          {required && <span className="ml-0.5 text-brand">*</span>}
        </label>
      )}
      {children}
      {error ? (
        <p className="text-xs font-medium text-danger">{error}</p>
      ) : hint ? (
        <p className="text-xs text-ink-faint">{hint}</p>
      ) : null}
    </div>
  )
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean
  suffix?: string
  icon?: ReactNode
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid, suffix, icon, ...props },
  ref,
) {
  const control = (
    <input
      ref={ref}
      className={cn(
        CONTROL,
        'h-10',
        icon && 'pl-9',
        suffix && 'pr-12',
        invalid && INVALID_CONTROL,
        className,
      )}
      {...props}
    />
  )

  if (!suffix && !icon) return control

  return (
    <div className="relative">
      {icon && (
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint">
          {icon}
        </span>
      )}
      {control}
      {suffix && (
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-ink-faint">
          {suffix}
        </span>
      )}
    </div>
  )
})

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, invalid, ...props },
  ref,
) {
  return (
    <textarea
      ref={ref}
      rows={3}
      className={cn(CONTROL, 'py-2 leading-relaxed', invalid && INVALID_CONTROL, className)}
      {...props}
    />
  )
})
